import { Badge, Card, Flex, Heading, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { JsonViewer } from 'components/common/json/json-viewer';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { first } from 'lodash';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const JsonPrinter = (props: {
  header: string;
  subheader?: string;
  value: any;
  fallback?: ReactNode;
}) => {
  // looks nicer to print w/ keys sorted
  const sortedObject = useMemo(
    () => MiscHelper.getSortedObject(props.value),
    [props.value]
  );

  return (
    <Flex direction="column" p={RADIX.FLEX.PAD.SM} gap={RADIX.FLEX.GAP.SM}>
      <Flex justify="between" gap="2">
        <Heading size={RADIX.HEADING.SIZE.SM}>{t(props.header)}</Heading>
        {props.subheader && <Badge>{t(props.subheader)}</Badge>}
      </Flex>

      <Card>
        {!sortedObject &&
          (props.fallback ?? <Text color={RADIX.COLOR.NEUTRAL}>None</Text>)}
        {sortedObject && <JsonViewer value={sortedObject} />}
      </Card>
    </Flex>
  );
};

interface IProps {
  pitch: IPitch;
}

export const InspectorTab = (props: IProps) => {
  const { activeModel, getTrainingPitches } = useContext(MachineContext);

  const tms = useTrainedMsStore((s) => s.tmsDict[props.pitch.bs_hash]);

  const [rebuiltMs, setRebuiltMs] = useState<IMachineState>();

  useEffect(() => {
    if (props.pitch.debugMs) {
      setRebuiltMs(undefined);
      return;
    }

    getTrainingPitches([props.pitch], {
      forceRebuild: true,
    }).then((results) => {
      const rebuilt = first(results);
      setRebuiltMs(rebuilt?._mx_ms);
    });
  }, [props.pitch]);

  return (
    <ErrorBoundary identifier="InspectorTab">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.SM}
        height={RADIX.DIALOG.HEIGHT.MD}
        overflowX="hidden"
        overflowY="auto"
      >
        {!props.pitch.debugMs && (
          <JsonPrinter
            header="Trained Machine State"
            subheader={tms ? 'From Context' : undefined}
            value={tms}
            fallback={
              <Text color={RADIX.COLOR.SECONDARY}>
                Please train this pitch first.
              </Text>
            }
          />
        )}

        {rebuiltMs && (
          <JsonPrinter
            header="Rebuilt Machine State"
            subheader={`Model: ${activeModel?.name}`}
            value={rebuiltMs}
            fallback={
              <Text color={RADIX.COLOR.WARNING}>
                Please check the active model (e.g. does it support building
                pitches by {props.pitch.priority}).
              </Text>
            }
          />
        )}

        {props.pitch.debugMs && (
          <JsonPrinter
            header="Debug Machine State"
            value={props.pitch.debugMs}
          />
        )}

        <JsonPrinter
          header="Pitch Ball State"
          subheader={`BS Hash: ${props.pitch.bs_hash ?? MachineHelper.getBsHash(props.pitch.bs, props.pitch.priority)}`}
          value={props.pitch.bs}
        />

        <JsonPrinter header="Pitch Trajectory" value={props.pitch.traj} />
      </Flex>
    </ErrorBoundary>
  );
};
